// Verrechnet den Regalplan (Soll) mit dem gezaehlten Bestand (Ist).
import type { RegalFach } from './handregal-geometrie'
import type { FachBelegungDto } from '@/api/warehouse'

export type FachStatus = 'gezaehlt' | 'offen' | 'frei'
export type BelegungsStatus = 'belegt' | 'frei' | 'unklar' | 'ungenutzt'

export interface BestandZeile {
  merchant_sku: string
  artikel_name: string | null
  qty: number
  counted_at: string | null
}

export interface KartenFach extends RegalFach {
  belegung: BelegungsStatus
  kopfName: string | null
  status: FachStatus
  gezaehltSku: string | null
  qty: number | null
  artikelName: string | null
  countedAt: string | null
}

export interface Verrechnung {
  karte: KartenFach[]
  fortschritt: { belegt: number; gezaehlt: number; offen: number }
  /** Zaehlungen, deren Sternnummer im Regalplan nicht vorkommt. */
  unbekannt: BestandZeile[]
  /** Zaehlungen, die auf dieselbe Fachnummer normalisieren wie eine andere Zeile und deshalb
   *  NICHT auf der Karte stehen. Beispiel: '35' und '035' sind in der Datenbank zwei Zeilen
   *  (UNIQUE liegt auf dem Rohwert), zeigen aber auf dasselbe Fach. Ohne diese Liste
   *  verschwaende die unterlegene Zeile spurlos — genau die stille Luecke, die diese Karte
   *  aufdecken soll. */
  mehrdeutig: BestandZeile[]
  gezaehltAberFrei: BestandZeile[]
}

/** Fachnummer aus einer Haendlerartikelnummer. Im Regalplan steht `35`, in den Daten kann
 *  `*035` stehen — ohne diese Angleichung zeigte die Karte gezaehlte Faecher als ungezaehlt.
 *  Alles, was keine reine Zahl ist, gehoert nicht ins Handregal-Raster und liefert null. */
export function normalisiereSternnummer(roh: string): number | null {
  const match = /^(\*{0,2})(\d+)$/.exec(roh.trim())
  if (!match) return null
  const n = Number(match[2])
  return Number.isSafeInteger(n) && n > 0 && (match[1] !== '**' || n <= 9) ? n : null
}

function istBesser(candidate: BestandZeile, existing: BestandZeile): boolean {
  if (candidate.counted_at !== null && existing.counted_at === null) return true
  if (candidate.counted_at === null && existing.counted_at !== null) return false

  if (candidate.counted_at !== null && existing.counted_at !== null) {
    const tCand = new Date(candidate.counted_at).getTime()
    const tExist = new Date(existing.counted_at).getTime()
    if (tCand > tExist) return true
    if (tCand < tExist) return false
  }

  return candidate.merchant_sku < existing.merchant_sku
}

export function verrechne(faecher: RegalFach[], bestand: BestandZeile[],
  belegung: Map<number, FachBelegungDto>, ungenutzt: (nummer: number) => boolean): Verrechnung {
  const nachNummer = new Map<number, BestandZeile>()
  const bekannteNummern = new Set(faecher.map((f) => f.nummer))

  for (const z of bestand) {
    const n = normalisiereSternnummer(z.merchant_sku)
    if (n === null || !bekannteNummern.has(n)) continue
    const vorh = nachNummer.get(n)
    if (!vorh || istBesser(z, vorh)) {
      nachNummer.set(n, z)
    }
  }

  const unbekannt: BestandZeile[] = []
  const mehrdeutig: BestandZeile[] = []
  const gezaehltAberFrei: BestandZeile[] = []

  for (const z of bestand) {
    const n = normalisiereSternnummer(z.merchant_sku)
    if (n === null || !bekannteNummern.has(n)) {
      unbekannt.push(z)
    } else if (nachNummer.get(n) !== z) {
      mehrdeutig.push(z)
    }
  }

  const karte: KartenFach[] = faecher.map((f) => {
    let z = nachNummer.get(f.nummer)
    const live = belegung.get(f.nummer)
    const belegungsStatus: BelegungsStatus = ungenutzt(f.nummer) ? 'ungenutzt' : live?.status ?? 'frei'
    // Eine 0-Zaehlung auf einem freien Fach bestaetigt, dass es leer ist (Nutzerregel 13.10).
    if (z?.qty === 0 && belegungsStatus === 'frei') z = undefined
    // Reihenfolge der Pruefung ist bewusst: eine Zaehlung mit Bestand gewinnt immer, auch auf
    // einem laut Plan freien Fach — sonst verschwiege die Karte eine echte Buchung.
    const status: FachStatus = z ? 'gezaehlt' : ['belegt', 'unklar'].includes(belegungsStatus) ? 'offen' : 'frei'
    if (z && belegungsStatus === 'frei') gezaehltAberFrei.push(z)
    return {
      ...f,
      belegung: belegungsStatus,
      kopfName: live?.kopf?.name ?? null,
      status,
      gezaehltSku: z?.merchant_sku ?? null,
      qty: z ? z.qty : null,
      artikelName: z?.artikel_name ?? null,
      countedAt: z?.counted_at ?? null,
    }
  })

  const belegt = karte.filter((f) => f.belegung === 'belegt' || f.belegung === 'unklar')
  return {
    karte,
    fortschritt: {
      belegt: belegt.length,
      gezaehlt: belegt.filter((f) => f.status === 'gezaehlt').length,
      offen: belegt.filter((f) => f.status === 'offen').length,
    },
    unbekannt,
    mehrdeutig,
    gezaehltAberFrei,
  }
}

